import { createBrowserRouter, type RouteObject } from 'react-router';

import { ROUTES } from '@/config/routes';
import { authRoutes } from '@/features/auth/routes';
import { notificationRoutes } from '@/features/notification/routes';
import { announcementRoutes } from '@/features/announcement/routes';
import { certificationRoutes } from '@/features/certification-guide/routes';
import { calendarRoutes } from '@/features/calendar/routes';
import { guideRoutes } from '@/features/guide/routes';
import { companyRoutes } from '@/features/company/routes';
import { consultantAnnouncementRoutes } from '@/features/consultant-announcement/routes';
import { dashboardRoutes } from '@/features/dashboard/routes';
import { finderRoutes } from '@/features/finder/routes';
import { landingRoutes } from '@/features/landing/routes';
import { publicSiteRoutes } from '@/features/public-site/routes';
import { diagnosisRoutes } from '@/features/diagnosis/routes';
import { trialRoutes } from '@/features/trial/routes';
import { legalRoutes } from '@/features/legal/routes';
import { WorkspaceEntryRedirect } from '@/features/mobile/components/WorkspaceEntryRedirect';
import { MobileLayout } from '@/features/mobile/layouts/MobileLayout';
import { mobileRoutes } from '@/features/mobile/routes';
import { companyJudgementRoutes } from '@/features/company-judgement/routes';
import { myCompanyRoutes } from '@/features/my-company/routes';
import { myRoadmapRoutes } from '@/features/my-roadmap/routes';
import { programRoutes } from '@/features/program/routes';
import { subscriptionRoutes } from '@/features/subscription/routes';
import { NotFoundPage } from '@/features/system/pages/NotFoundPage';
import { RouteErrorPage } from '@/features/system/pages/RouteErrorPage';
import { FoBlankLayout, FoContentContainer, FoDefaultLayout, FoMinimalLayout } from '@/layouts/fo';
import { RequireRouteFeature, RequireWorkspaceMember, WorkspaceLayout } from '@/layouts/workspace';

import { LoadingState } from '@/components/common/LoadingState';

import { RootLayout } from './RootLayout';

/**
 * 라우트 트리 — 레이아웃별 그룹. 각 feature 는 `routes.tsx` 로 자기 라우트를 소유하고 여기서 조립만 한다.
 * - FO 풀셸(default)  : 랜딩·법무
 * - FO 미니멀        : 인증 폼·상태 카드
 * - FO 블랭크        : SSO 콜백
 * - /workspace       : 워크스페이스 회원 가드 + 4분할 레이아웃(Body 만 교체) + 라우트 handle.feature 기능 가드(specs/064)
 * - /workspace/m     : 같은 가드 + 모바일 레이아웃(specs/037 — 현장용 등록·조회·공고 검색)
 */
const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteErrorPage />,
    // lazy 라우트 초기 로드 동안의 폴백(없으면 react-router 가 경고)
    HydrateFallback: () => <LoadingState fullscreen />,
    children: [
      {
        element: <FoDefaultLayout />,
        // 공개 화면(specs/070) — 둘러보기·올해 꼭 보는 사업·기업 인증 안내(워크스페이스와 같은 화면, 비로그인)
        children: [
          ...landingRoutes,
          ...legalRoutes,
          ...publicSiteRoutes,
          // 워크스페이스와 공유하는 화면은 자체 컨테이너가 없어 여기서 폭·여백을 준다
          { element: <FoContentContainer />, children: certificationRoutes() },
        ],
      },
      {
        element: <FoMinimalLayout />,
        children: [
          ...authRoutes.minimal,
          ...trialRoutes,
          ...diagnosisRoutes,
          ...notificationRoutes,
        ],
      },
      { element: <FoBlankLayout />, children: authRoutes.blank },
      {
        path: ROUTES.workspace.root,
        element: <RequireWorkspaceMember />,
        children: [
          // 모바일 화면(specs/037) — PC 레이아웃과 형제. 가드·세션은 공유한다.
          {
            path: 'm',
            element: <MobileLayout />,
            children: [{ element: <RequireRouteFeature />, children: mobileRoutes }],
          },
          {
            element: <WorkspaceLayout />,
            children: [
              // 첫 화면 = 허용된 첫 메뉴(모바일 기기면 첫 모바일 탭) — 지도사 공고 찾기·기업 홈(specs/037 FR-004 · specs/065 FR-008).
              // 로그인 전 요청 경로는 useLoginDestination 이 먼저 처리한다.
              { index: true, element: <WorkspaceEntryRedirect /> },
              {
                element: <RequireRouteFeature />,
                children: [
                  ...myCompanyRoutes,
                  ...myRoadmapRoutes,
                  ...companyJudgementRoutes,
                  ...finderRoutes,
                  ...dashboardRoutes,
                  ...calendarRoutes,
                  ...companyRoutes,
                  ...programRoutes,
                  ...announcementRoutes,
                  ...consultantAnnouncementRoutes,
                  ...subscriptionRoutes,
                  ...guideRoutes,
                  ...certificationRoutes(),
                ],
              },
            ],
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
